import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Github, Linkedin, Mail, Sparkles } from 'lucide-react';
import { personalInfo } from '../data/mock';
import { Button } from './ui/button';

const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0a0a0a]">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 grid-background opacity-30"></div>
      
      {/* Floating Elements */}
      <motion.div
        className="absolute top-20 left-10 w-20 h-20 bg-white/5 rounded-lg backdrop-blur-sm"
        animate={{
          y: [0, -20, 0],
          rotate: [0, 5, 0]
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-32 right-20 w-32 h-32 bg-white/5 rounded-full backdrop-blur-sm"
        animate={{
          y: [0, 20, 0],
          scale: [1, 1.1, 1]
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-20">
        {/* Profile Image with Animation */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, type: "spring" }}
          className="mb-8 flex justify-center"
        >
          <div className="relative">
            <motion.div
              className="absolute inset-0 rounded-full bg-gradient-to-r from-white/20 to-white/5 blur-2xl"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0.8, 0.5]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <img
              src={personalInfo.profileImage}
              alt={personalInfo.name}
              className="relative w-32 h-32 rounded-full border-4 border-white/10 object-cover shadow-2xl"
            />
          </div>
        </motion.div>

        {/* Name with Reveal Animation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="text-center"
        >
          <h1 className="text-5xl md:text-7xl font-bold mb-4 tracking-tight">
            {personalInfo.name}
          </h1>
        </motion.div>

        {/* Title with Gradient */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mb-6 space-y-2 text-center"
        >
          <h2 className="text-2xl md:text-3xl font-semibold text-gray-200">
            {personalInfo.title}
          </h2>
          <div className="flex items-center justify-center gap-2">
            <Sparkles size={18} className="text-gray-400" />
            <p className="text-lg md:text-xl text-gray-400 monospace">
              {personalInfo.subtitle}
            </p>
          </div>
        </motion.div>

        {/* Bio with Stagger */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="text-base md:text-lg text-gray-400 max-w-3xl mx-auto mb-10 leading-relaxed text-center"
        >
          {personalInfo.bio}
        </motion.p>

        {/* CTA Buttons with Hover Effects */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="flex flex-wrap gap-4 justify-center mb-10"
        >
          <Button
            onClick={() => scrollToSection('projects')}
            className="bg-white text-black hover:bg-gray-100 font-semibold px-8 py-6 text-base group transition-all"
          >
            View Projects
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
          </Button>
          <Button
            onClick={() => scrollToSection('blogs')}
            variant="outline"
            className="border-2 border-white/20 text-white hover:bg-white hover:text-black font-semibold px-8 py-6 text-base transition-all"
          >
            View Blogs
          </Button>
        </motion.div>

        {/* Social Links with Hover Effects */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="flex gap-6 justify-center"
        >
          {[
            { icon: Github, href: personalInfo.social.github },
            { icon: Linkedin, href: personalInfo.social.linkedin },
            { icon: Mail, href: `mailto:${personalInfo.email}` }
          ].map((social, index) => (
            <motion.a
              key={index}
              href={social.href}
              className="text-gray-400 hover:text-white transition-colors p-3 rounded-full hover:bg-white/5"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <social.icon size={24} />
            </motion.a>
          ))}
        </motion.div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0a] to-transparent"></div>
    </section>
  );
};

export default Hero;